import type {
  BrandId,
  BulkUpsertSalesCostsRequest,
  BulkUpsertSalesCostsResponse,
  ListSalesCostsResponse,
  UpsertSalesCostRequest,
  UpsertSalesCostResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrappers + React Query hooks for `/v1/ops/sales-costs`
 * (epic #1032 E3-P1, #1102) — the lead pricing panel data layer.
 *
 * Brand handling mirrors the server contract:
 *   - lead sessions omit `brand` (ops-api pins their profile brand);
 *   - admin sessions pass an explicit brand.
 *
 * The bulk mutation drives the ATOMIC platform endpoint — one request
 * for N SKUs, all-or-nothing — replacing the old per-SKU frontend
 * fan-out pattern for this path.
 */

const BASE_PATH = '/v1/ops/sales-costs';

export const SALES_COSTS_QUERY_KEY = ['sales-costs'] as const;

function withBrand(path: string, brand: BrandId | null): string {
  return brand === null ? path : `${path}?brand=${encodeURIComponent(brand)}`;
}

export async function listSalesCosts(
  brand: BrandId | null,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListSalesCostsResponse> {
  return opsApiRequest<ListSalesCostsResponse>(withBrand(BASE_PATH, brand), { signal }, fetchImpl);
}

export async function upsertSalesCost(
  skuId: string,
  brand: BrandId | null,
  body: UpsertSalesCostRequest,
  fetchImpl: typeof fetch,
): Promise<UpsertSalesCostResponse> {
  return opsApiRequest<UpsertSalesCostResponse>(
    withBrand(`${BASE_PATH}/${encodeURIComponent(skuId)}`, brand),
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function bulkUpsertSalesCosts(
  brand: BrandId | null,
  body: BulkUpsertSalesCostsRequest,
  fetchImpl: typeof fetch,
): Promise<BulkUpsertSalesCostsResponse> {
  return opsApiRequest<BulkUpsertSalesCostsResponse>(
    withBrand(`${BASE_PATH}/bulk`, brand),
    { method: 'POST', body },
    fetchImpl,
  );
}

/**
 * `enabled` gates the fetch until the session has resolved — the page
 * cannot know whether to send an explicit admin brand before it knows
 * the session roles, and an early brand-less admin request would 400.
 */
export function useSalesCosts(
  brand: BrandId | null,
  enabled = true,
): UseQueryResult<ListSalesCostsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...SALES_COSTS_QUERY_KEY, brand ?? 'own'],
    queryFn: ({ signal }) => listSalesCosts(brand, fetchImpl, signal),
    enabled,
  });
}

export interface UpsertSalesCostVariables {
  skuId: string;
  brand: BrandId | null;
  body: UpsertSalesCostRequest;
}

export function useUpsertSalesCost(): UseMutationResult<
  UpsertSalesCostResponse,
  Error,
  UpsertSalesCostVariables
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId, brand, body }) => upsertSalesCost(skuId, brand, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SALES_COSTS_QUERY_KEY });
    },
  });
}

export interface BulkUpsertSalesCostsVariables {
  brand: BrandId | null;
  body: BulkUpsertSalesCostsRequest;
}

export function useBulkUpsertSalesCosts(): UseMutationResult<
  BulkUpsertSalesCostsResponse,
  Error,
  BulkUpsertSalesCostsVariables
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ brand, body }) => bulkUpsertSalesCosts(brand, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SALES_COSTS_QUERY_KEY });
    },
  });
}
